import { Instance, types, flow } from "mobx-state-tree";
import axios from "axios";
import {
  SCHEDULES_API,
  HOLIDAYS_API,
  NOT_TAKING_REPORTS_API,
} from "@constants/api";
import { AlertResultApi } from "@utils/alert";
import { fetching, FetchingEnum } from "./Fetching";

export const initialSchedule = {
  fetching: FetchingEnum.DONE,
  fetchingHoliday: FetchingEnum.DONE,
  fetchingNotTaking: FetchingEnum.DONE,
};

const School = types.model({
  id: types.number,
  name: types.string,
  address: types.maybeNull(types.string),
  lat: types.number,
  lng: types.number,
});

const Location = types.model({
  id: types.number,
  address: types.maybeNull(types.string),
  lat: types.number,
  lng: types.number,
  arrivedAt: types.string,
  remark: types.maybeNull(types.string),
});

const ScheduleSchool = types.model({
  school: School,
  routeType: types.string,
  routeName: types.string,
  location: Location,
  startedAt: types.string,
  endedAt: types.string,
  isNotTakingReported: types.boolean,
  assignmentId: types.maybeNull(types.number),
  busSchedulePlanId: types.maybeNull(types.number),
  assignmentLogs: types.optional(
    types.array(
      types.model({
        type: types.string,
        loggedAt: types.string,
      })
    ),
    []
  ),
});

const Schedule = types.model({
  id: types.number,
  name: types.string,
  photoUrl: types.maybeNull(types.string),
  isMain: types.boolean,
  schedules: types.maybeNull(types.array(ScheduleSchool)),
});

const Holiday = types.model({
  id: types.number,
  schoolId: types.number,
  academicYearId: types.number,
  name: types.string,
  date: types.string,
  type: types.string,
  createdAt: types.string,
  updatedAt: types.string,
});

export const Schedules = types
  .model({
    listSchedules: types.maybeNull(types.array(Schedule)),
    fetching: fetching,
    status: types.maybeNull(types.number),
    holidays: types.maybeNull(types.array(Holiday)),
    fetchingHoliday: fetching,
    statusHoliday: types.maybeNull(types.number),
    fetchingNotTaking: fetching,
    statusNotTaking: types.maybeNull(types.number),
  })
  .actions((self) => ({
    getListSchedules: flow(function* (token: string, dateString: string) {
      self.fetching = "pending";
      self.listSchedules = null;
      self.status = null;
      try {
        const result = yield axios.get(`${SCHEDULES_API}/${dateString}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("getListSchedules", result);
        self.fetching = "done";
        self.listSchedules = result.data;
      } catch (error) {
        console.error("Failed to fetch projects", error);
        self.fetching = "error";
        self.listSchedules = null;
        self.status = error?.response?.status || 500;
        AlertResultApi(
          error?.response?.status || 500,
          error.response?.data?.message
        );
      }
    }),
    getListHolidays: flow(function* (
      token: string,
      fromDate: string,
      toDate: string
    ) {
      self.fetchingHoliday = "pending";
      self.holidays = null;
      self.statusHoliday = null;
      try {
        const result = yield axios.get(
          `${HOLIDAYS_API}?FromDate=${fromDate}&ToDate=${toDate}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        console.log("getListHolidays", result);
        self.fetchingHoliday = "done";
        self.holidays = result.data;
      } catch (error) {
        console.error("Failed to fetch projects", error);
        self.fetchingHoliday = "error";
        self.statusHoliday = error?.response?.status || 500;
        AlertResultApi(
          error?.response?.status || 500,
          error.response?.data?.message
        );
      }
    }),
    postNotTakingReported: flow(function* (
      token: string,
      scheduleId: number[],
      studentId: number,
      date: string,
      callback: () => void
    ) {
      self.fetchingNotTaking = "pending";
      self.statusNotTaking = null;
      try {
        const result = yield axios.request({
          method: "post",
          url: NOT_TAKING_REPORTS_API,
          headers: { Authorization: `Bearer ${token}` },
          data: {
            busSchedulePlanIds: scheduleId,
            studentId: studentId,
            date: date,
          },
        });
        console.log("postNotTakingReported", result);
        self.fetchingNotTaking = "done";
        self.statusNotTaking = result.status;
        callback();
      } catch (error) {
        console.error("Failed to fetch projects", error);
        self.fetchingNotTaking = "error";
        self.statusNotTaking = error?.response?.status || 500;
        AlertResultApi(
          error?.response?.status || 500,
          error.response?.data?.message
        );
      }
    }),
  }))
  .views((self) => ({
    getViewHoliday: (yearMonth: string) => {
      // console.log(month)
      let holidays: Instance<typeof Holiday>[] = [];
      self.holidays?.forEach((holiday) => {
        const date = `${holiday.date.split("-")[0]} ${
          holiday.date.split("-")[1]
        }`;
        if (date === yearMonth) {
          holidays.push(holiday);
        }
      });
      return holidays;
    },
    listHolidays: (monthYear: string) => {
      let tempListHolidays: Date[] = [];
      self.holidays?.forEach((holiday) => {
        const monthYearSplit = `${holiday.date.split("-")[0]} ${
          holiday.date.split("-")[1]
        }`;
        if (monthYearSplit === monthYear) {
          tempListHolidays.push(new Date(holiday.date));
        }
      });
      return tempListHolidays;
    },
  }));

export type SchedulesType = Instance<typeof Schedules>;
export type ScheduleType = Instance<typeof Schedule>;
export type LocationType = Instance<typeof Location>;
export type SchoolType = Instance<typeof School>;
